/**
 * CommandPickerPopover — the shared searchable, grouped picker for VQE-Edit:
 * every "add …" list (columns, tables, joins, sort / partition columns,
 * RETURNING and INSERT columns). A `cmdk` Command gives fuzzy search +
 * keyboard nav + group headings for free, so a multi-table query no longer
 * renders one undifferentiated wall of columns.
 *
 * Preserves the VQE-RO contract: the trigger stays `disabled` (with a "why
 * disabled" tooltip) until VQE-Edit supplies `disabled={false}` + groups +
 * onSelect. cmdk filters items by their `value`; selection ignores the cmdk
 * value and reports the item's stable `key` via a closure so callers can map
 * straight back to their domain object.
 */

import { useRef, useState, type ReactNode } from 'react';
import { ChevronDown, Plus, type LucideIcon } from 'lucide-react';

import { Popover, PopoverContent, PopoverTrigger } from '../../../../../components/ui/popover';
import { Button } from '../../../../../components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../../../components/ui/tooltip';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandList } from '../../../../../components/ui/command';
import { cn } from '../../../../../lib/utils';
import { vqeCopy } from '../../ux/vqeCopy';
import { CustomEntryRow, GroupHeadingWithAction, PickerItemRow, ScopeToggleRow } from './CommandPickerParts';

export interface CommandPickerItem {
  /** Stable id reported to `onSelect` (independent of the cmdk search value). */
  readonly key: string;
  /** Lower-case-friendly haystack cmdk filters on. Must be unique per item. Defaults to a string `label` + the unique `key`. */
  readonly value?: string;
  readonly label: ReactNode;
  /** Muted trailing text — e.g. a column's data type. */
  readonly meta?: ReactNode;
  /** Leading/trailing badges — e.g. PK / FK chips. */
  readonly badges?: ReactNode;
  /**
   * Already in the clause the picker adds to (SELECT list, ORDER BY, GROUP BY):
   * `true` shows a check mark, `false` keeps the check column empty so rows
   * stay aligned. Omit for pickers that don't track membership.
   */
  readonly checked?: boolean;
  /** Listed but not pickable (e.g. a column the SET list already assigns). */
  readonly disabled?: boolean;
}

/**
 * A "Use “typed text”" row: while the search holds text that no item's label
 * matches exactly, the list ends with it, so a name the schema doesn't list
 * (a column the snapshot hasn't loaded, a computed name) can still be picked.
 * It is the only row — and so the Enter target — when nothing else matches.
 */
export interface CommandPickerCustomEntry {
  readonly onPick: (text: string) => void;
  /** Muted trailing text on the row. */
  readonly meta?: string;
  /** Hide the row for text the caller refuses (e.g. a column the list already has). */
  readonly accept?: (text: string) => boolean;
}

export interface CommandPickerGroupAction {
  /** Stable id reported to `onGroupAction`. */
  readonly key: string;
  /** Short label shown at the right of the heading — e.g. "+ all 7". */
  readonly label: string;
  readonly tooltip?: string;
}

export interface CommandPickerGroup {
  readonly heading?: string;
  readonly items: readonly CommandPickerItem[];
  /** Optional bulk action shown at the right of the group heading row. */
  readonly action?: CommandPickerGroupAction;
}

/** A footer toggle that flips the picker between "tables in query" and "all". */
export interface CommandPickerScopeToggle {
  readonly showingAll: boolean;
  readonly onToggle: () => void;
}

export interface CommandPickerPopoverProps {
  readonly testId: string;
  readonly triggerLabel: string;
  /** Trigger glyph — defaults to `+` (every add affordance); a swap action passes its own. */
  readonly triggerIcon?: LucideIcon;
  readonly groups?: readonly CommandPickerGroup[];
  /** A flat list — one heading-less group — instead of `groups`. */
  readonly items?: readonly CommandPickerItem[];
  readonly disabled?: boolean;
  readonly tooltip?: string;
  readonly defaultOpen?: boolean;
  /**
   * Fired whenever the popover's open state changes (trigger toggle, Escape,
   * outside click). Forwarded verbatim to the underlying Radix `Popover.Root`
   * — the open state stays owned by this component (`defaultOpen` seeds it),
   * this is purely a notification so callers can react to a dismiss without a pick (e.g.
   * `AddBuilder` resetting a pending aggregate template). Optional — existing
   * callers that don't pass it see no behavior change.
   */
  readonly onOpenChange?: (open: boolean) => void;
  readonly onSelect?: (key: string) => void;
  /**
   * Close the popover after a pick (item or group action). For single-pick
   * affordances (add column / sort / group-by) — left open, the list kept
   * covering the controls below it, so the next click landed on another list
   * item. Multi-step pickers (the filter builder re-shows this picker for its
   * second step) leave it off.
   */
  readonly closeOnSelect?: boolean;
  /** Fired when a group's bulk `action` is clicked, with the action's key. */
  readonly onGroupAction?: (key: string) => void;
  readonly loading?: boolean;
  /** Body when there is nothing to list. Defaults to the "no matches in scope" hint. */
  readonly emptyMessage?: string;
  /** Muted note under a non-empty list — e.g. which sources' columns are not listed. */
  readonly notice?: string;
  readonly searchPlaceholder?: string;
  /** Optional footer toggle to widen the picker from in-query tables to all. */
  readonly scopeToggle?: CommandPickerScopeToggle;
  /**
   * Optional content rendered flush at the top of the popover, above the
   * search box — e.g. the AND/OR connector toggle for the WHERE add affordance.
   * Only shown in the active (non-disabled) picker states.
   */
  readonly header?: ReactNode;
  /**
   * Trigger emphasis. `'ghost'` (default) is the quiet "+ add" link every
   * clause card uses. `'primary'` is a filled button for a surface where the
   * picker IS the main action (the starter canvas's Start from a table) — a
   * ghost trigger there reads as disabled even when it is enabled. `'chip'` is
   * the small dashed "+ or" / "+ and" chip on a WHERE / HAVING condition.
   */
  readonly triggerVariant?: 'ghost' | 'primary' | 'chip' | 'inline';
  /**
   * Extra trigger classes. The `'inline'` variant — a name that is itself the
   * picker (a SET column, the compact header's table) — takes its typography
   * and colour from here, so it reads exactly like the text it replaces.
   */
  readonly triggerClassName?: string;
  /** Tooltip and accessible name while enabled (a short chip label needs one). */
  readonly hint?: string;
  /** See `CommandPickerCustomEntry`. */
  readonly customEntry?: CommandPickerCustomEntry;
  /** Popover width override (default `w-72`) — e.g. a list of long schema-qualified names. */
  readonly contentClassName?: string;
}

type TriggerVariant = NonNullable<CommandPickerPopoverProps['triggerVariant']>;

const TRIGGER_CLASS: Record<TriggerVariant, string> = {
  ghost: 'h-7 gap-1 px-2 text-xs text-muted-foreground transition-transform active:scale-[0.96]',
  primary: 'gap-1 transition-transform active:scale-[0.96]',
  chip:
    'h-5 gap-0.5 rounded-full border border-dashed border-muted-foreground/40 bg-card px-1.5 text-[11px] font-semibold text-muted-foreground hover:border-solid hover:border-primary hover:bg-primary/10 hover:text-primary data-[state=open]:border-solid data-[state=open]:border-primary data-[state=open]:text-primary',
  // No max-width here: with the -mx-1 bleed a percentage cap clamps the name
  // narrower than itself. min-w-0 + shrink still let a tight header truncate it.
  inline:
    'group/inline -mx-1 h-auto min-w-0 shrink justify-start gap-0.5 rounded px-1 py-0 has-[>svg]:px-1 hover:text-current focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-offset-0 data-[state=open]:bg-accent',
};

const DEFAULT_TOOLTIP = vqeCopy.disabled.editingUnavailable;
const DEFAULT_EMPTY_MESSAGE = 'No matches in scope. Connect to a database or refresh the schema cache.';
const SCAFFOLDING_BODY = vqeCopy.disabled.columnEditingUnavailable;

export function CommandPickerPopover({
  testId,
  triggerLabel,
  triggerIcon: TriggerIcon = Plus,
  items,
  groups = items ? [{ items }] : [],
  disabled = true,
  tooltip = DEFAULT_TOOLTIP,
  defaultOpen = false,
  onOpenChange,
  onSelect,
  closeOnSelect = false,
  onGroupAction,
  loading = false,
  emptyMessage = DEFAULT_EMPTY_MESSAGE,
  notice,
  searchPlaceholder = 'Search…',
  scopeToggle,
  header,
  triggerVariant = 'ghost',
  triggerClassName,
  hint,
  customEntry,
  contentClassName = 'w-72',
}: CommandPickerPopoverProps) {
  const primaryTrigger = triggerVariant === 'primary';
  const totalItems = groups.reduce((n, g) => n + g.items.length, 0);
  const [open, setOpen] = useState(defaultOpen);
  const [search, setSearch] = useState('');
  // When the list closes, Radix hands focus back to the trigger; that focus
  // must not pop the trigger's tooltip (it would hang over the toolbar).
  const closedAt = useRef(0);
  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) {
      setSearch('');
      closedAt.current = Date.now();
    }
    onOpenChange?.(next);
  };
  const customText = customEntry ? search.trim() : '';
  const showCustom =
    customText.length > 0 &&
    (customEntry?.accept?.(customText) ?? true) &&
    !groups.some((g) =>
      g.items.some((item) => typeof item.label === 'string' && item.label.toLowerCase() === customText.toLowerCase()),
    );
  const handleSelect = (key: string) => {
    onSelect?.(key);
    if (closeOnSelect) handleOpenChange(false);
  };
  const handleGroupAction = (key: string) => {
    onGroupAction?.(key);
    if (closeOnSelect) handleOpenChange(false);
  };
  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <Tooltip>
        {/* Span-wrapped trigger: a `disabled` Button is pointer-events:none, so
            the tooltip trigger can't sit on it directly or hover (and the "why
            disabled" reason) would never fire. */}
        <TooltipTrigger asChild>
          <span
            className={triggerVariant === 'inline' ? 'inline-flex min-w-0' : 'inline-flex'}
            // preventDefault here skips Radix's open-on-focus handler, which runs after it.
            onFocus={(e) => {
              if (Date.now() - closedAt.current < 600) e.preventDefault();
            }}
          >
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant={primaryTrigger ? 'default' : 'ghost'}
                size="sm"
                className={cn(TRIGGER_CLASS[triggerVariant], triggerClassName)}
                data-trigger-variant={triggerVariant}
                disabled={disabled}
                aria-disabled={disabled}
                aria-label={!disabled && hint ? hint : undefined}
                data-testid={testId}
              >
                {triggerVariant === 'inline' ? (
                  // The name itself is the trigger: label, then a quiet chevron.
                  <>
                    <span className="truncate">{triggerLabel}</span>
                    <ChevronDown aria-hidden className="size-3 opacity-50 group-hover/inline:opacity-100" />
                  </>
                ) : (
                  <>
                    <TriggerIcon className={triggerVariant === 'chip' ? 'h-3 w-3' : 'h-3.5 w-3.5'} />
                    {triggerLabel}
                  </>
                )}
              </Button>
            </PopoverTrigger>
          </span>
        </TooltipTrigger>
        {disabled ? (
          <TooltipContent side="top">{tooltip}</TooltipContent>
        ) : hint ? (
          <TooltipContent side="top">{hint}</TooltipContent>
        ) : null}
      </Tooltip>
      <PopoverContent
        align="start"
        className={`${contentClassName} p-0 text-xs`}
        data-testid={`${testId}-content`}
        data-disabled={disabled ? 'true' : 'false'}
        data-loading={loading ? 'true' : 'false'}
        // A late schema-refresh re-render can shift focus and fire Radix's
        // focus-outside dismiss mid-selection (this is a nested, body-portalled
        // popover over the Monaco editor). Outside pointer-down still dismisses.
        onFocusOutside={(e) => e.preventDefault()}
      >
        {/* Header (e.g. AND/OR connector toggle) sits flush above the search box
            in every active state. Never shown on the disabled RO scaffold. */}
        {!disabled && header ? header : null}
        {disabled ? (
          <p className="p-3 text-muted-foreground" data-testid={`${testId}-body`}>
            {SCAFFOLDING_BODY}
          </p>
        ) : loading ? (
          <p className="p-3 italic text-muted-foreground" data-testid={`${testId}-loading`}>
            Loading suggestions…
          </p>
        ) : totalItems === 0 && !customEntry ? (
          <p className="p-3 italic text-muted-foreground" data-testid={`${testId}-empty`}>
            {emptyMessage}
          </p>
        ) : (
          <Command>
            {/* Uncontrolled: cmdk filters synchronously on input, so a typed name
                + an immediate Enter picks the right row; we only mirror the text. */}
            <CommandInput
              placeholder={searchPlaceholder}
              data-testid={`${testId}-search`}
              className="h-8"
              onValueChange={setSearch}
            />
            <CommandList data-testid={`${testId}-items`}>
              {totalItems === 0 && !showCustom ? (
                <p className="px-3 py-2 italic text-muted-foreground" data-testid={`${testId}-empty`}>
                  {emptyMessage}
                </p>
              ) : null}
              {/* cmdk's Empty shows whenever nothing matches — with no items at
                  all that is always, so only offer it over a real list. */}
              {totalItems > 0 && !showCustom ? (
                <CommandEmpty data-testid={`${testId}-no-match`}>No matches.</CommandEmpty>
              ) : null}
              {groups.map((group, gi) => (
                <CommandGroup
                  key={group.heading ?? gi}
                  heading={
                    group.action && onGroupAction ? (
                      <GroupHeadingWithAction
                        testId={testId}
                        heading={group.heading}
                        action={group.action}
                        onGroupAction={handleGroupAction}
                      />
                    ) : (
                      group.heading
                    )
                  }
                >
                  {group.items.map((item) => (
                    <PickerItemRow key={item.key} testId={testId} item={item} onSelect={handleSelect} />
                  ))}
                </CommandGroup>
              ))}
              {showCustom && customEntry ? (
                <CustomEntryRow
                  testId={testId}
                  text={customText}
                  meta={customEntry.meta}
                  onPick={() => {
                    customEntry.onPick(customText);
                    if (closeOnSelect) handleOpenChange(false);
                  }}
                />
              ) : null}
            </CommandList>
            {notice ? (
              <p
                className="border-t border-border px-2 py-1.5 text-[11px] italic text-muted-foreground"
                data-testid={`${testId}-notice`}
              >
                {notice}
              </p>
            ) : null}
            {scopeToggle ? <ScopeToggleRow testId={testId} toggle={scopeToggle} /> : null}
          </Command>
        )}
      </PopoverContent>
    </Popover>
  );
}
